import { Stack } from '@mui/material';
import React from 'react';

import { TaskItemDto } from '@/api/generated';
import {
  RowDetailsItem,
  RowDetailsLayout,
  RowDetailsLink,
  RowDetailsMuted,
  RowDetailsOpenButton,
  RowDetailsSection,
  RowDetailsText,
} from '@/components/table/expandable/RowDetails.comp.tsx';
import { SmartDate } from '@/components/ui/smartDate/SmartDate.comp.tsx';
import { isTaskFinished } from '@/components/ui/statusPill/statusTone.util.ts';
import { UserAvatar, UserAvatarGroup } from '@/components/ui/userAvatar/UserAvatar.comp.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { taskContextResourcePath } from '@/views/tasks/list/utils/taskContextResourcePath.util.ts';

type Props = {
  detailsPath: string;
  row: TaskItemDto;
};

export const TasksListRowDetails: React.FC<Props> = ({ detailsPath, row }) => {
  const { t } = useTranslationWithPrefix('views.tasks.list.table.details');
  const { t: tBody } = useTranslationWithPrefix('views.tasks.list.table.body');
  const { t: tSections } = useTranslationWithPrefix('components.listDetails.sections');
  const resourceLabel = tBody(`resourceTypeOptions.${row.contextResourceType}`);
  const resourcePath = taskContextResourcePath(row.contextResourceType, row.contextResourceId);

  return (
    <RowDetailsLayout actions={<RowDetailsOpenButton label={t('open')} to={detailsPath} />}>
      <RowDetailsSection title={t('description')}>
        {row.description?.trim() ? (
          <RowDetailsText lines={6} text={row.description} />
        ) : (
          <RowDetailsMuted text={t('noDescription')} />
        )}
      </RowDetailsSection>
      <RowDetailsSection title={t('people')}>
        <RowDetailsItem label={t('creator')}>
          <UserAvatar person={row.creator} size={22} withName />
        </RowDetailsItem>
        <RowDetailsItem label={t('assignedUser')}>
          <UserAvatar person={row.assignedUser} size={22} withName />
        </RowDetailsItem>
        <RowDetailsItem label={t('watchers')}>
          <Stack direction="row">
            <UserAvatarGroup persons={row.watchersUsers} role={t('watchers')} size={22} />
          </Stack>
        </RowDetailsItem>
      </RowDetailsSection>
      <RowDetailsSection title={tSections('details')}>
        <RowDetailsItem label={tBody('dueDate')}>
          <SmartDate date={row.dueDate} isFinished={isTaskFinished(row.status)} variant="due" />
        </RowDetailsItem>
        <RowDetailsItem label={t('updatedAt')}>
          <SmartDate date={row.updatedAt} />
        </RowDetailsItem>
        <RowDetailsItem label={t('contextResource')}>
          {resourcePath ? (
            <RowDetailsLink to={resourcePath}>{resourceLabel}</RowDetailsLink>
          ) : (
            resourceLabel
          )}
        </RowDetailsItem>
      </RowDetailsSection>
    </RowDetailsLayout>
  );
};
